import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  DocumentData,
  QueryConstraint,
  Unsubscribe,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import firebaseConfig from '../../firebase-applet-config.json';
import {
  Product,
  Order,
  OrderStatus,
  StoreSettings,
  CartItem,
  CustomerDetails,
  CustomerReview,
  HeroSlide,
  MediaItem,
} from '../types/store';
import {
  PRODUCTS as INITIAL_PRODUCTS,
  ASSET_IMAGES,
  getProductMainImage,
  isValidProductionImageUrl,
  normalizeProductImageUrl,
} from '../data/products';
import { CUSTOMER_REVIEWS as INITIAL_REVIEWS } from '../data/mockData';
import { STORE_CONFIG } from '../config/storeConfig';
import { getSavedMarketingAttribution } from './analytics';
import { setStoreLogo, isValidLogoUrl } from '../constants/branding';
import { sendAdminOrderNotification } from './emailNotification';

// 1. Firebase App, Auth, Firestore & Storage Initialization
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

export const auth = getAuth(app);

// Use initializeFirestore with forced long-polling for instant connection across sandboxes, proxies, and preview containers
const dbId =
  firebaseConfig.firestoreDatabaseId && firebaseConfig.firestoreDatabaseId !== '(default)'
    ? firebaseConfig.firestoreDatabaseId
    : undefined;

let firestoreInstance;
try {
  firestoreInstance = initializeFirestore(
    app,
    {
      experimentalForceLongPolling: true,
    },
    dbId
  );
} catch {
  firestoreInstance = dbId ? getFirestore(app, dbId) : getFirestore(app);
}

export const db = firestoreInstance;

export const storage = getStorage(app);

/**
 * Universal recursive sanitizer that removes all `undefined` values and invalid fields
 * from objects and arrays, ensuring strict compatibility with Firestore WriteBatch, setDoc, and updateDoc.
 */
export function cleanFirestoreData<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return null as unknown as T;
  }
  if (typeof obj !== 'object') {
    return obj;
  }
  if (obj instanceof Date) {
    return obj.toISOString() as unknown as T;
  }
  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== undefined)
      .map((item) => (typeof item === 'object' && item !== null ? cleanFirestoreData(item) : item)) as unknown as T;
  }

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        result[key] = cleanFirestoreData(value);
      } else if (Array.isArray(value)) {
        result[key] = value
          .filter((item) => item !== undefined)
          .map((item) => (typeof item === 'object' && item !== null ? cleanFirestoreData(item) : item));
      } else {
        result[key] = value;
      }
    }
  }
  return result as T;
}

// Authentication Helpers
export async function logInAdmin(email: string, password: string): Promise<User> {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

export async function registerAdminUser(email: string, password: string): Promise<User> {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

export async function logOutAdmin(): Promise<void> {
  await firebaseSignOut(auth);
}

export function subscribeAuthState(callback: (user: User | null) => void): Unsubscribe {
  return onAuthStateChanged(auth, callback);
}

// Persistent Admin Account Management in Firestore
export interface AdminAccountRecord {
  id?: string;
  email: string;
  password?: string;
  displayName: string;
  role: 'super_admin' | 'admin' | 'owner';
  isConfigured: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

const formatAdminDocId = (email: string) => email.trim().toLowerCase().replace(/[^a-zA-Z0-9]/g, '_');

export async function getAdminAccountDb(email: string): Promise<AdminAccountRecord | null> {
  try {
    const docId = formatAdminDocId(email);
    const docRef = doc(db, 'admin_accounts', docId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() } as AdminAccountRecord;
    }
    return null;
  } catch (err) {
    console.warn('Error fetching admin account from db:', err);
    return null;
  }
}

export async function saveAdminAccountDb(
  email: string,
  password?: string,
  displayName?: string,
  role: 'super_admin' | 'admin' | 'owner' = 'super_admin'
): Promise<AdminAccountRecord> {
  const cleanEmail = email.trim().toLowerCase();
  const docId = formatAdminDocId(cleanEmail);
  const now = new Date().toISOString();
  
  const existing = await getAdminAccountDb(cleanEmail);
  
  const record: AdminAccountRecord = {
    email: cleanEmail,
    displayName: displayName || existing?.displayName || cleanEmail.split('@')[0],
    role: role || existing?.role || 'super_admin',
    isConfigured: true,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    lastLoginAt: now,
  };

  if (password) {
    record.password = password;
  } else if (existing?.password) {
    record.password = existing.password;
  }

  try {
    const docRef = doc(db, 'admin_accounts', docId);
    await setDoc(docRef, record, { merge: true });
  } catch (err) {
    console.warn('Could not persist admin account to Firestore:', err);
  }

  return record;
}

export async function verifyAdminCredentialsDb(email: string, password: string): Promise<{ valid: boolean; account?: AdminAccountRecord }> {
  const cleanEmail = email.trim().toLowerCase();
  const cleanPass = password.trim();
  
  // 1. Check in Firestore admin_accounts collection
  try {
    const account = await getAdminAccountDb(cleanEmail);
    if (account && account.password && account.password === cleanPass) {
      // Update last login timestamp
      saveAdminAccountDb(cleanEmail, cleanPass, account.displayName, account.role).catch(() => {});
      return { valid: true, account };
    }
  } catch (err) {
    console.warn('Firestore admin verification warning:', err);
  }

  // 2. Built-in super admin authorized accounts fallback
  const authorizedEmails = ['zohaibarshad72722@gmail.com', 'abdulsamikhan121212@gmail.com', 'admin@malrich.pk'];
  if (authorizedEmails.includes(cleanEmail)) {
    if (cleanPass === 'storeadmin123' || cleanPass === 'MalRich2026!Admin') {
      const fallbackAccount: AdminAccountRecord = {
        email: cleanEmail,
        displayName: cleanEmail.includes('zohaib') ? 'Zohaib Arshad (Super Admin)' : 'Abdul Sami Khan (Owner)',
        role: 'super_admin',
        isConfigured: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return { valid: true, account: fallbackAccount };
    }
  }

  return { valid: false };
}

// 2. Generic Centralized CRUD Helper Functions
export async function getDocument<T = DocumentData>(
  collectionName: string,
  docId: string
): Promise<T | null> {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as unknown as T;
    }
    return null;
  } catch (err) {
    console.warn(`Error in getDocument for ${collectionName}/${docId}:`, err);
    return null;
  }
}

export async function getCollection<T = DocumentData>(
  collectionName: string,
  ...queryConstraints: QueryConstraint[]
): Promise<T[]> {
  try {
    const colRef = collection(db, collectionName);
    const q = queryConstraints.length > 0 ? query(colRef, ...queryConstraints) : colRef;
    const snapshot = await getDocs(q);
    const results: T[] = [];
    snapshot.forEach((d) => {
      results.push({ id: d.id, ...d.data() } as unknown as T);
    });
    return results;
  } catch (err) {
    console.warn(`Error in getCollection for ${collectionName}:`, err);
    return [];
  }
}

export async function setDocument<T extends Record<string, any>>(
  collectionName: string,
  docId: string,
  data: T,
  merge: boolean = true
): Promise<void> {
  const docRef = doc(db, collectionName, docId);
  const cleaned = cleanFirestoreData(data);
  await setDoc(docRef, cleaned, { merge });
}

export async function updateDocument<T extends Record<string, any>>(
  collectionName: string,
  docId: string,
  data: Partial<T>
): Promise<void> {
  const docRef = doc(db, collectionName, docId);
  const cleaned = cleanFirestoreData(data);
  await updateDoc(docRef, cleaned as DocumentData);
}

export async function removeDocument(
  collectionName: string,
  docId: string
): Promise<void> {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
}

export function subscribeToCollection<T = DocumentData>(
  collectionName: string,
  onUpdate: (items: T[]) => void,
  onError?: (error: Error) => void,
  ...queryConstraints: QueryConstraint[]
): Unsubscribe {
  const colRef = collection(db, collectionName);
  const q = queryConstraints.length > 0 ? query(colRef, ...queryConstraints) : colRef;

  return onSnapshot(
    q,
    (snapshot) => {
      const items: T[] = [];
      snapshot.forEach((d) => {
        items.push({ id: d.id, ...d.data() } as unknown as T);
      });
      onUpdate(items);
    },
    (err) => {
      console.warn(`Firestore subscription error on ${collectionName}:`, err);
      if (onError) onError(err);
    }
  );
}

// 3. Products Services
const PRODUCTS_COLLECTION = 'products';
const LOCAL_STORAGE_KEY = 'healthstore_custom_products';

export function hydrateProductWithLiveAssets(product: Product): Product {
  // 1. Gather all valid production images from the product
  const candidateImages: string[] = [];
  
  if (Array.isArray(product.images) && product.images.length > 0) {
    product.images.forEach((img) => {
      if (isValidProductionImageUrl(img)) {
        const normalized = normalizeProductImageUrl(img, product.flavor, product.id, product.title);
        if (normalized && !candidateImages.includes(normalized)) {
          candidateImages.push(normalized);
        }
      }
    });
  }

  // Check singular legacy fields ONLY if images array was empty
  if (candidateImages.length === 0) {
    const anyProd = product as Record<string, any>;
    ['image', 'imageUrl', 'mainImage', 'photo'].forEach((key) => {
      const val = anyProd[key];
      if (isValidProductionImageUrl(val)) {
        const normalized = normalizeProductImageUrl(val, product.flavor, product.id, product.title);
        if (normalized && !candidateImages.includes(normalized)) {
          candidateImages.push(normalized);
        }
      }
    });
  }

  // 2. If user provided valid images, preserve EXACTLY those images.
  // Only use default catalog image if product has NO images at all (slot is empty).
  const finalImages = candidateImages.length > 0 ? candidateImages : [getProductMainImage(product)];

  // 3. Clean and sanitize variants - ALWAYS guaranteed to be an Array, NEVER undefined!
  const rawVariants = Array.isArray(product.variants) ? product.variants : [];
  const sanitizedVariants = rawVariants
    .filter((v) => v && typeof v === 'object')
    .map((v) => {
      const vImg = isValidProductionImageUrl(v.image)
        ? normalizeProductImageUrl(v.image, v.flavor, v.sku || product.id, v.title)
        : (finalImages[0] || getProductMainImage({ flavor: v.flavor, title: v.title }));
      return {
        ...v,
        title: v.title || product.title || '',
        flavor: v.flavor || product.flavor || 'Vanilla',
        weight: v.weight || product.weight || '400g',
        image: vImg,
        price: Number(v.price) || Number(product.price) || 0,
        compareAtPrice: Number(v.compareAtPrice || product.compareAtPrice || v.price || 0),
        inStock: v.inStock !== false,
        sku: v.sku || `SKU-${product.id || 'MAL'}-${Date.now()}`,
      };
    });

  const hydrated: Product = {
    ...product,
    id: product.id || `malrich-${Date.now()}`,
    title: product.title || '',
    brand: product.brand || 'MalRich',
    flavor: product.flavor || 'Vanilla',
    weight: product.weight || '400g',
    price: Number(product.price) || 0,
    compareAtPrice: Number(product.compareAtPrice || product.price || 0),
    inStock: product.inStock !== false,
    images: finalImages,
    variants: sanitizedVariants,
    keyNutrients: Array.isArray(product.keyNutrients) ? product.keyNutrients : [],
    benefits: Array.isArray(product.benefits) ? product.benefits : [],
    category: product.category || 'Supplements',
    shortDescription: product.shortDescription || '',
    description: product.description || '',
    usageInstructions: product.usageInstructions || '',
    storageInfo: product.storageInfo || '',
    badge: product.badge || 'Sale',
    rating: typeof product.rating === 'number' ? product.rating : 0,
    reviewCount: typeof product.reviewCount === 'number' ? product.reviewCount : 0,
    isBestSeller: Boolean(product.isBestSeller),
  };

  return cleanFirestoreData(hydrated);
}

const DELETED_PRODUCTS_KEY = 'healthstore_deleted_products_ids_v2';

export function getDeletedProductIds(): string[] {
  try {
    const saved = localStorage.getItem(DELETED_PRODUCTS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.warn('Could not read deleted products:', e);
  }
  return [];
}

export function addDeletedProductId(id: string): void {
  try {
    const current = getDeletedProductIds();
    if (!current.includes(id)) {
      const updated = [...current, id];
      localStorage.setItem(DELETED_PRODUCTS_KEY, JSON.stringify(updated));
    }
  } catch (e) {
    console.warn('Could not save deleted product id:', e);
  }
}

export function removeDeletedProductId(id: string): void {
  try {
    const current = getDeletedProductIds();
    const updated = current.filter((item) => item !== id);
    localStorage.setItem(DELETED_PRODUCTS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.warn('Could not remove deleted product id:', e);
  }
}

export function clearDeletedProductIds(): void {
  try {
    localStorage.removeItem(DELETED_PRODUCTS_KEY);
  } catch (e) {
    console.warn('Could not clear deleted products:', e);
  }
}

export function getLocalCachedProducts(): Product[] {
  const deletedIds = getDeletedProductIds();
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
          .filter((p) => p && p.id && !deletedIds.includes(p.id))
          .map(hydrateProductWithLiveAssets);
      }
    }
  } catch (e) {
    console.warn('Could not read from localStorage:', e);
  }
  return INITIAL_PRODUCTS.filter((p) => !deletedIds.includes(p.id));
}

export function setLocalCachedProducts(products: Product[]): void {
  try {
    const deletedIds = getDeletedProductIds();
    const filtered = (products || []).filter((p) => p && p.id && !deletedIds.includes(p.id));
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtered));
  } catch (e) {
    console.warn('Could not save to localStorage:', e);
  }
}

export async function fetchProductsFromDb(): Promise<Product[]> {
  const deletedIds = getDeletedProductIds();
  try {
    const products = await getCollection<Product>(PRODUCTS_COLLECTION);
    if (!products || products.length === 0) {
      const cached = getLocalCachedProducts();
      if (cached && cached.length > 0) {
        await seedProductsDb(cached);
        return cached;
      }
      const initialNonDeleted = INITIAL_PRODUCTS.filter((p) => !deletedIds.includes(p.id));
      await seedProductsDb(initialNonDeleted);
      return initialNonDeleted;
    }
    const hydrated = products
      .filter((p) => p && p.id && !deletedIds.includes(p.id))
      .map(hydrateProductWithLiveAssets);
    setLocalCachedProducts(hydrated);
    return hydrated;
  } catch (err) {
    console.error('Error in fetchProductsFromDb:', err);
    return getLocalCachedProducts();
  }
}

export function subscribeProducts(
  onData: (products: Product[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  return subscribeToCollection<Product>(
    PRODUCTS_COLLECTION,
    (products) => {
      const deletedIds = getDeletedProductIds();
      if (products.length === 0) {
        const cached = getLocalCachedProducts();
        if (cached && cached.length > 0) {
          seedProductsDb(cached).catch(console.error);
          onData(cached);
        } else {
          const initialNonDeleted = INITIAL_PRODUCTS.filter((p) => !deletedIds.includes(p.id));
          seedProductsDb(initialNonDeleted).catch(console.error);
          onData(initialNonDeleted);
        }
      } else {
        const hydrated = products
          .filter((p) => p && p.id && !deletedIds.includes(p.id))
          .map(hydrateProductWithLiveAssets);
        setLocalCachedProducts(hydrated);
        onData(hydrated);
      }
    },
    (err) => {
      onData(getLocalCachedProducts());
      if (onError) onError(err);
    }
  );
}

export async function saveProductDb(product: Product): Promise<void> {
  const id = product.id || `malrich-${Date.now()}`;
  removeDeletedProductId(id);

  const prepared: Product = {
    ...product,
    id,
    slug: product.slug || product.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    inStock: product.inStock !== false,
  };

  const list = getLocalCachedProducts();
  const index = list.findIndex((p) => p.id === id);
  const updated = index >= 0 ? [...list] : [prepared, ...list];
  if (index >= 0) updated[index] = prepared;
  setLocalCachedProducts(updated);

  await setDocument(PRODUCTS_COLLECTION, id, prepared);
}

export async function updateProductFieldDb(productId: string, partialFields: Partial<Product>): Promise<void> {
  removeDeletedProductId(productId);
  const list = getLocalCachedProducts();
  const index = list.findIndex((p) => p.id === productId);
  if (index >= 0) {
    const updated = [...list];
    updated[index] = { ...updated[index], ...partialFields };
    setLocalCachedProducts(updated);
  }

  await updateDocument(PRODUCTS_COLLECTION, productId, partialFields);
}

export async function deleteProductDb(productId: string, imageUrls?: string[]): Promise<void> {
  // 1. Permanently blacklist from ever being resurrected by default fallbacks
  addDeletedProductId(productId);

  // 2. Remove from local cache
  const list = getLocalCachedProducts();
  setLocalCachedProducts(list.filter((p) => p.id !== productId));
  
  // 3. Safely clean up storage images in background
  if (imageUrls && imageUrls.length > 0) {
    imageUrls.forEach((imgUrl) => {
      if (imgUrl && imgUrl.includes('firebasestorage.googleapis.com')) {
        deleteFileFromStorage(imgUrl).catch(() => {});
      }
    });
  }

  // 4. Permanently remove document from Firestore database
  await removeDocument(PRODUCTS_COLLECTION, productId);
}

export async function seedProductsDb(products: Product[]): Promise<void> {
  const deletedIds = getDeletedProductIds();
  const nonDeleted = (products || []).filter((p) => p && p.id && !deletedIds.includes(p.id));

  try {
    const batch = writeBatch(db);
    const cleanedProducts = nonDeleted.map(hydrateProductWithLiveAssets);
    cleanedProducts.forEach((prod) => {
      const docRef = doc(db, PRODUCTS_COLLECTION, prod.id);
      batch.set(docRef, cleanFirestoreData(prod), { merge: true });
    });
    await batch.commit();
    setLocalCachedProducts(cleanedProducts);
  } catch (e) {
    console.warn('Notice seeding products to Firestore:', e);
    setLocalCachedProducts(nonDeleted.map(hydrateProductWithLiveAssets));
  }
}

export async function resetCatalogDb(): Promise<void> {
  try {
    clearDeletedProductIds();
    const current = await getCollection<Product>(PRODUCTS_COLLECTION);
    const batch = writeBatch(db);
    current.forEach((prod) => {
      batch.delete(doc(db, PRODUCTS_COLLECTION, prod.id));
    });
    await batch.commit();
    await seedProductsDb(INITIAL_PRODUCTS);
  } catch (e) {
    console.error('Error resetting catalog:', e);
    setLocalCachedProducts(INITIAL_PRODUCTS);
  }
}

// 4. Real Orders Service in Firestore with Multi-Key Recovery & Auto-Sync
const ORDERS_COLLECTION = 'orders';
const ORDERS_LOCAL_KEY = 'healthstore_orders_v2';
const ORDERS_BACKUP_KEY = 'healthstore_orders_backup';

// Known historical localStorage keys to scan and restore from
const HISTORICAL_ORDER_KEYS = [
  'healthstore_orders_v2',
  'healthstore_orders_backup',
  'healthstore_orders',
  'healthstore_orders_v1',
  'healthstore_saved_orders',
  'orders',
  'malrich_orders',
  'healthstore_pending_orders',
];

/**
 * Universal Order Parser & Sanitizer
 */
function isValidOrderObject(obj: any): obj is Order {
  return (
    obj &&
    typeof obj === 'object' &&
    (typeof obj.id === 'string' || typeof obj.orderNumber === 'string') &&
    obj.customer &&
    (typeof obj.customer.fullName === 'string' || typeof obj.customer.phone === 'string') &&
    Array.isArray(obj.items)
  );
}

/**
 * Multi-source local cache scanner: Scans all current and historical localStorage keys,
 * merges, deduplicates, and restores all past orders without any loss.
 */
export function getLocalCachedOrders(): Order[] {
  if (typeof window === 'undefined') return [];

  const orderMap = new Map<string, Order>();

  HISTORICAL_ORDER_KEYS.forEach((key) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          parsed.forEach((item) => {
            if (isValidOrderObject(item)) {
              const uniqueKey = item.id || item.orderNumber;
              if (!orderMap.has(uniqueKey)) {
                orderMap.set(uniqueKey, item);
              }
            }
          });
        }
      }
    } catch {
      // ignore
    }
  });

  const orders = Array.from(orderMap.values()).sort((a, b) => {
    const timeA = a.timestamp || (a.createdAt ? new Date(a.createdAt).getTime() : 0) || 0;
    const timeB = b.timestamp || (b.createdAt ? new Date(b.createdAt).getTime() : 0) || 0;
    return timeB - timeA;
  });

  return orders;
}

export function setLocalCachedOrders(orders: Order[]): void {
  if (typeof window === 'undefined') return;
  try {
    const validOrders = (orders || []).filter(isValidOrderObject);
    const jsonStr = JSON.stringify(validOrders);
    localStorage.setItem(ORDERS_LOCAL_KEY, jsonStr);
    localStorage.setItem(ORDERS_BACKUP_KEY, jsonStr);

    // Immediately dispatch an in-app event so all open views update with 0ms delay
    window.dispatchEvent(new CustomEvent('healthstore_orders_updated', { detail: validOrders }));
  } catch (e) {
    console.warn('Could not save cached orders:', e);
  }
}

/**
 * Merges Firestore remote orders with all local cached orders, ensuring no orders
 * are ever lost if Firestore returns empty or partial data.
 */
export function mergeOrdersLists(remoteOrders: Order[], localOrders: Order[]): Order[] {
  const map = new Map<string, Order>();

  // 1. First add all local orders
  (localOrders || []).forEach((ord) => {
    if (isValidOrderObject(ord)) {
      const key = ord.id || ord.orderNumber;
      map.set(key, ord);
    }
  });

  // 2. Overlay remote Firestore orders (takes precedence on status/timeline if updated)
  (remoteOrders || []).forEach((ord) => {
    if (isValidOrderObject(ord)) {
      const key = ord.id || ord.orderNumber;
      map.set(key, ord);
    }
  });

  // 3. Sort newest first
  return Array.from(map.values()).sort((a, b) => {
    const timeA = a.timestamp || (a.createdAt ? new Date(a.createdAt).getTime() : 0) || 0;
    const timeB = b.timestamp || (b.createdAt ? new Date(b.createdAt).getTime() : 0) || 0;
    return timeB - timeA;
  });
}

/**
 * Restores and synchronizes all orders between localStorage and Cloud Firestore.
 * Automatically uploads any local-only orders to Firestore and saves all to localStorage.
 */
export async function restoreAndSyncAllOrdersDb(): Promise<Order[]> {
  try {
    const localOrders = getLocalCachedOrders();
    const remoteOrders = await getCollection<Order>(ORDERS_COLLECTION);
    const merged = mergeOrdersLists(remoteOrders, localOrders);

    // Save consolidated orders back to localStorage
    setLocalCachedOrders(merged);

    // Upload any missing orders to Firestore in background
    const remoteIds = new Set((remoteOrders || []).map((o) => o.id || o.orderNumber));
    const missingInRemote = merged.filter((o) => !remoteIds.has(o.id) && !remoteIds.has(o.orderNumber));

    if (missingInRemote.length > 0) {
      for (const missingOrder of missingInRemote) {
        try {
          const docId = missingOrder.id || `ord_${Date.now()}_${Math.floor(1000 + Math.random() * 9000)}`;
          await setDocument(ORDERS_COLLECTION, docId, { ...missingOrder, id: docId });
        } catch (e) {
          console.warn('Sync order upload error:', e);
        }
      }
    }

    return merged;
  } catch (err) {
    console.error('Error in restoreAndSyncAllOrdersDb:', err);
    return getLocalCachedOrders();
  }
}

export function subscribeOrdersDb(
  onData: (orders: Order[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  // Immediately emit cached orders so UI shows orders without delay
  const initialCached = getLocalCachedOrders();
  if (initialCached && initialCached.length > 0) {
    onData(initialCached);
  }

  // Real-time in-app event listener for instantaneous UI updates
  const handleLocalUpdate = (e: any) => {
    if (e?.detail && Array.isArray(e.detail)) {
      onData(e.detail);
    } else {
      onData(getLocalCachedOrders());
    }
  };

  // Real-time cross-tab storage listener for multi-tab updates
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === ORDERS_LOCAL_KEY || e.key === ORDERS_BACKUP_KEY) {
      onData(getLocalCachedOrders());
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('healthstore_orders_updated', handleLocalUpdate);
    window.addEventListener('storage', handleStorageChange);
  }

  // Subscribe to real-time updates from Firestore
  const unsubFirestore = subscribeToCollection<Order>(
    ORDERS_COLLECTION,
    (remoteOrders) => {
      const currentLocal = getLocalCachedOrders();
      const merged = mergeOrdersLists(remoteOrders, currentLocal);

      // Persist merged orders to localStorage
      setLocalCachedOrders(merged);

      // Auto-sync any local-only orders back to Firestore if missing
      const remoteIds = new Set((remoteOrders || []).map((o) => o.id || o.orderNumber));
      const unsynced = merged.filter((o) => !remoteIds.has(o.id) && !remoteIds.has(o.orderNumber));
      if (unsynced.length > 0) {
        unsynced.forEach((ord) => {
          const docId = ord.id || `ord_${Date.now()}_${Math.floor(1000 + Math.random() * 9000)}`;
          setDocument(ORDERS_COLLECTION, docId, { ...ord, id: docId }).catch(() => {});
        });
      }

      onData(merged);
    },
    (err) => {
      console.warn('Firestore orders subscription fallback to local cache:', err);
      const fallback = getLocalCachedOrders();
      onData(fallback);
      if (onError) onError(err);
    }
  );

  return () => {
    unsubFirestore();
    if (typeof window !== 'undefined') {
      window.removeEventListener('healthstore_orders_updated', handleLocalUpdate);
      window.removeEventListener('storage', handleStorageChange);
    }
  };
}

export async function fetchOrdersDb(): Promise<Order[]> {
  try {
    const remoteOrders = await getCollection<Order>(ORDERS_COLLECTION);
    const localOrders = getLocalCachedOrders();
    const merged = mergeOrdersLists(remoteOrders, localOrders);
    setLocalCachedOrders(merged);
    return merged;
  } catch (err) {
    console.error('Error in fetchOrdersDb:', err);
    return getLocalCachedOrders();
  }
}

/**
 * Server-safe Price Verification and Order Placement into Firestore
 */
export async function createOrderDb(
  customer: CustomerDetails,
  rawItems: CartItem[],
  customDiscount: number = 0
): Promise<Order> {
  // 1. Fetch current authoritative product records to prevent client-side price manipulation
  const currentProducts = await fetchProductsFromDb();

  let trustedSubtotal = 0;
  const verifiedItems: CartItem[] = rawItems.map((cartItem) => {
    const matchedProduct = currentProducts.find((p) => p.id === cartItem.productId);
    const trustedPrice = matchedProduct ? matchedProduct.price : cartItem.price;
    const trustedCompare = matchedProduct ? matchedProduct.compareAtPrice : cartItem.compareAtPrice;
    const trustedImage = matchedProduct && matchedProduct.images && matchedProduct.images[0]
      ? matchedProduct.images[0]
      : cartItem.image;

    const lineTotal = trustedPrice * cartItem.quantity;
    trustedSubtotal += lineTotal;

    return {
      ...cartItem,
      price: trustedPrice,
      compareAtPrice: trustedCompare,
      image: trustedImage,
    };
  });

  // Calculate Shipping with threshold
  const settings = await getStoreSettingsDb();
  const freeThreshold = settings.freeShippingThreshold || STORE_CONFIG.freeShippingThreshold;
  const standardShipping = settings.standardShippingFee ?? STORE_CONFIG.standardShippingFee;
  const hasFreeShipping = trustedSubtotal >= freeThreshold;
  const shippingFee = verifiedItems.length === 0 ? 0 : hasFreeShipping ? 0 : standardShipping;
  const discount = Math.max(0, customDiscount);
  const total = Math.max(0, trustedSubtotal - discount + shippingFee);

  // Generate Unique Human-Readable Sequential/Timestamped Order ID
  const orderCount = (getLocalCachedOrders().length || 0) + 1;
  const randomSuffix = Math.floor(1000 + Math.random() * 9000);
  const orderNumber = `HS-${10000 + orderCount}-${randomSuffix}`;
  const now = new Date();
  const timestamp = now.getTime();
  const dateStr = now.toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const marketingAttribution = getSavedMarketingAttribution();

  const newOrder: Order = {
    id: `ord_${timestamp}_${randomSuffix}`,
    orderNumber,
    createdAt: dateStr,
    timestamp,
    customer: {
      fullName: customer.fullName.trim(),
      phone: customer.phone.trim(),
      email: customer.email?.trim() || '',
      address: customer.address.trim(),
      city: customer.city.trim(),
      notes: customer.notes?.trim() || '',
    },
    items: verifiedItems,
    subtotal: trustedSubtotal,
    shippingFee,
    discount,
    total,
    paymentMethod: 'Cash on Delivery',
    status: 'New',
    courier: 'TCS Express Pakistan (COD)',
    trackingNumber: `TCS-PK-${randomSuffix}`,
    estimatedDelivery: '2–4 Business Days',
    notes: customer.notes || '',
    marketingAttribution,
    timeline: [
      {
        status: 'New',
        title: 'Order Registered in System',
        date: dateStr,
        description: 'Your Cash on Delivery order has been created and received by Health Store fulfillment.',
        completed: true,
      },
      {
        status: 'Confirmed',
        title: 'Order Confirmed by Store Team',
        date: 'Pending',
        description: 'Customer contact details and shipping address verified.',
        completed: false,
      },
      {
        status: 'Processing',
        title: 'Warehouse Packaging & Quality Inspection',
        date: 'Pending',
        description: 'Genuine MalRich supplement sealed and packed for dispatch.',
        completed: false,
      },
      {
        status: 'Shipped',
        title: 'Dispatched via TCS Courier',
        date: 'Pending',
        description: 'Parcel in transit with Cash on Delivery invoice.',
        completed: false,
      },
      {
        status: 'Delivered',
        title: 'Successfully Delivered to Customer',
        date: 'Pending',
        description: 'Cash collected upon delivery and order completed.',
        completed: false,
      },
    ],
  };

  // Save to Firestore
  try {
    await setDocument(ORDERS_COLLECTION, newOrder.id, newOrder);
  } catch (e) {
    console.error('Error saving order directly to Firestore:', e);
  }

  // Update local cache
  const existingOrders = getLocalCachedOrders();
  setLocalCachedOrders([newOrder, ...existingOrders]);

  // Permanently remember customer address for future auto-fill so it is never lost
  try {
    localStorage.setItem(
      'healthstore_saved_customer_shipping_v1',
      JSON.stringify({
        fullName: newOrder.customer.fullName,
        phone: newOrder.customer.phone,
        email: newOrder.customer.email || '',
        address: newOrder.customer.address,
        city: newOrder.customer.city || 'Karachi',
        notes: newOrder.customer.notes || '',
      })
    );
  } catch {
    // ignore
  }

  // Dispatch automated Admin Email notification
  try {
    sendAdminOrderNotification(newOrder).catch((err) => {
      console.warn('Background admin email notification dispatch issue:', err);
    });
  } catch (emailErr) {
    console.warn('Failed to trigger email notification:', emailErr);
  }

  return newOrder;
}

export async function updateOrderStatusDb(orderId: string, newStatus: OrderStatus): Promise<void> {
  const currentOrders = getLocalCachedOrders();
  const target = currentOrders.find((o) => o.id === orderId);
  const now = new Date().toLocaleDateString('en-PK', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const updatedTimeline = target?.timeline?.map((item) => {
    if (item.status === newStatus) {
      return { ...item, completed: true, date: now };
    }
    return item;
  }) || [];

  const updatePayload: Partial<Order> = {
    status: newStatus,
    timeline: updatedTimeline,
  };

  // Update local cache
  const updatedList = currentOrders.map((o) =>
    o.id === orderId ? { ...o, ...updatePayload } : o
  );
  setLocalCachedOrders(updatedList);

  // Update in Firestore
  await updateDocument(ORDERS_COLLECTION, orderId, updatePayload);
}

export async function deleteOrderDb(orderId: string): Promise<void> {
  const currentOrders = getLocalCachedOrders();
  const updatedList = currentOrders.filter((o) => o.id !== orderId);
  setLocalCachedOrders(updatedList);

  try {
    await removeDocument(ORDERS_COLLECTION, orderId);
  } catch (err) {
    console.error('Error removing order from Firestore:', err);
  }
}

// 5. Store Settings in Firestore
const SETTINGS_COLLECTION = 'settings';
const STORE_SETTINGS_DOC = 'store';

export const DEFAULT_STORE_SETTINGS: StoreSettings = {
  storeName: STORE_CONFIG.name,
  tagline: STORE_CONFIG.tagline,
  phone: '03229768391',
  whatsappNumber: '923229768391',
  email: STORE_CONFIG.email,
  address: STORE_CONFIG.address || 'Commercial Plaza, Karachi, Pakistan',
  city: 'Karachi',
  businessHours: STORE_CONFIG.businessHours || '24/7 Customer Support',
  standardShippingFee: 200,
  freeShippingThreshold: 2000,
  currency: 'PKR',
  logoUrl: '',
  metaPixelId: STORE_CONFIG.defaultMetaPixelId || '1041026968930278',
  metaPixelEnabled: true,
  tiktokPixelId: STORE_CONFIG.defaultTikTokPixelId || 'DA86HQBC77UES9745BGG',
  tiktokPixelEnabled: true,
  ga4Id: '',
  googleAdsId: '',
  lastUpdated: new Date().toISOString(),
  updatedBy: 'System Auto-Config',
};

export async function getStoreSettingsDb(): Promise<StoreSettings> {
  try {
    const docData = await getDocument<StoreSettings>(SETTINGS_COLLECTION, STORE_SETTINGS_DOC);
    if (docData) {
      const merged = { ...DEFAULT_STORE_SETTINGS, ...docData };
      if (merged.logoUrl && isValidLogoUrl(merged.logoUrl)) {
        setStoreLogo(merged.logoUrl);
      } else {
        setStoreLogo(null);
      }
      return merged;
    }
  } catch (err) {
    console.warn('Could not fetch store settings, using defaults', err);
  }
  return DEFAULT_STORE_SETTINGS;
}

export async function saveStoreSettingsDb(settings: Partial<StoreSettings>): Promise<void> {
  await setDocument(SETTINGS_COLLECTION, STORE_SETTINGS_DOC, settings, true);
  if (settings.logoUrl !== undefined) {
    setStoreLogo(isValidLogoUrl(settings.logoUrl) ? settings.logoUrl : null);
  }
}

export function subscribeStoreSettingsDb(callback: (settings: StoreSettings) => void): Unsubscribe {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, STORE_SETTINGS_DOC);
    return onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as StoreSettings;
        const merged = { ...DEFAULT_STORE_SETTINGS, ...data };
        if (merged.logoUrl && isValidLogoUrl(merged.logoUrl)) {
          setStoreLogo(merged.logoUrl);
        } else {
          setStoreLogo(null);
        }
        callback(merged);
      } else {
        setStoreLogo(null);
        callback(DEFAULT_STORE_SETTINGS);
      }
    }, (err) => {
      console.warn('Settings subscription fallback:', err);
      callback(DEFAULT_STORE_SETTINGS);
    });
  } catch (err) {
    console.warn('Could not subscribe to store settings:', err);
    return () => {};
  }
}

// 6. Reviews & Testimonials Firestore & Local Cache Service
const REVIEWS_COLLECTION = 'reviews';
const LOCAL_REVIEWS_KEY = 'healthstore_custom_reviews_v3';
const REVIEWS_INIT_KEY = 'healthstore_reviews_initialized_v3';

// Known initial/dummy demo review IDs
export const DUMMY_REVIEW_IDS = ['rev-1', 'rev-2', 'rev-3', 'rev-4', 'rev-5'];

export function isDummyReview(reviewId: string): boolean {
  return DUMMY_REVIEW_IDS.includes(reviewId) || reviewId.startsWith('dummy-');
}

export function getLocalCachedReviews(): CustomerReview[] {
  try {
    const saved = localStorage.getItem(LOCAL_REVIEWS_KEY);
    if (saved !== null) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Could not read reviews from localStorage:', e);
  }
  // If not explicitly set or cleared yet, check init flag
  const isInit = localStorage.getItem(REVIEWS_INIT_KEY);
  if (isInit === 'cleared') {
    return [];
  }
  return INITIAL_REVIEWS;
}

export function setLocalCachedReviews(reviews: CustomerReview[]): void {
  try {
    localStorage.setItem(LOCAL_REVIEWS_KEY, JSON.stringify(reviews));
    localStorage.setItem(REVIEWS_INIT_KEY, 'true');
  } catch (e) {
    console.warn('Could not save reviews to localStorage:', e);
  }
}

export async function fetchReviewsFromDb(): Promise<CustomerReview[]> {
  try {
    const isCleared = localStorage.getItem(REVIEWS_INIT_KEY) === 'cleared';
    const reviews = await getCollection<CustomerReview>(REVIEWS_COLLECTION);
    if (!reviews || reviews.length === 0) {
      if (isCleared) {
        return [];
      }
      // First time initialization only if never initialized or cleared
      const hasInit = localStorage.getItem(REVIEWS_INIT_KEY);
      if (!hasInit) {
        await seedReviewsDb(INITIAL_REVIEWS);
        return INITIAL_REVIEWS;
      }
      return [];
    }
    setLocalCachedReviews(reviews);
    return reviews;
  } catch (err) {
    console.error('Error in fetchReviewsFromDb:', err);
    return getLocalCachedReviews();
  }
}

export function subscribeReviewsDb(
  onData: (reviews: CustomerReview[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  let isFirstSnap = true;
  return subscribeToCollection<CustomerReview>(
    REVIEWS_COLLECTION,
    (reviews) => {
      const isCleared = localStorage.getItem(REVIEWS_INIT_KEY) === 'cleared';
      const hasInit = localStorage.getItem(REVIEWS_INIT_KEY);

      if (reviews.length === 0) {
        if (!hasInit && isFirstSnap && !isCleared) {
          // One-time initial seeding only if brand new database setup
          seedReviewsDb(INITIAL_REVIEWS).catch(console.error);
          onData(INITIAL_REVIEWS);
        } else {
          // User intentionally deleted reviews or database is empty - DO NOT reseed!
          setLocalCachedReviews([]);
          onData([]);
        }
      } else {
        setLocalCachedReviews(reviews);
        onData(reviews);
      }
      isFirstSnap = false;
    },
    (err) => {
      onData(getLocalCachedReviews());
      if (onError) onError(err);
    }
  );
}

export async function saveReviewDb(review: CustomerReview): Promise<void> {
  const id = review.id || `rev-${Date.now()}`;
  const prepared: CustomerReview = {
    ...review,
    id,
    rating: Number(review.rating) || 5,
    verifiedBuyer: review.verifiedBuyer !== false,
  };

  const list = getLocalCachedReviews();
  const index = list.findIndex((r) => r.id === id);
  const updated = index >= 0 ? [...list] : [prepared, ...list];
  if (index >= 0) updated[index] = prepared;
  setLocalCachedReviews(updated);

  await setDocument(REVIEWS_COLLECTION, id, prepared, true);
}

export async function deleteReviewDb(reviewId: string): Promise<void> {
  const list = getLocalCachedReviews();
  const updated = list.filter((r) => r.id !== reviewId);
  setLocalCachedReviews(updated);
  if (updated.length === 0) {
    localStorage.setItem(REVIEWS_INIT_KEY, 'cleared');
  }
  await removeDocument(REVIEWS_COLLECTION, reviewId);
}

export async function deleteAllDummyReviewsDb(): Promise<void> {
  try {
    const list = getLocalCachedReviews();
    const remaining = list.filter((r) => !isDummyReview(r.id));
    setLocalCachedReviews(remaining);
    if (remaining.length === 0) {
      localStorage.setItem(REVIEWS_INIT_KEY, 'cleared');
    }

    const batch = writeBatch(db);
    for (const dummyId of DUMMY_REVIEW_IDS) {
      batch.delete(doc(db, REVIEWS_COLLECTION, dummyId));
    }
    // Also delete any with 'dummy-' prefix
    for (const r of list) {
      if (isDummyReview(r.id)) {
        batch.delete(doc(db, REVIEWS_COLLECTION, r.id));
      }
    }
    await batch.commit();
  } catch (e) {
    console.error('Error deleting dummy reviews from Firestore:', e);
  }
}

export async function deleteAllReviewsDb(): Promise<void> {
  try {
    setLocalCachedReviews([]);
    localStorage.setItem(REVIEWS_INIT_KEY, 'cleared');
    const current = await getCollection<CustomerReview>(REVIEWS_COLLECTION);
    if (current && current.length > 0) {
      const batch = writeBatch(db);
      current.forEach((rev) => {
        batch.delete(doc(db, REVIEWS_COLLECTION, rev.id));
      });
      await batch.commit();
    }
  } catch (e) {
    console.error('Error deleting all reviews from Firestore:', e);
  }
}

export async function seedReviewsDb(reviews: CustomerReview[]): Promise<void> {
  try {
    const batch = writeBatch(db);
    reviews.forEach((rev) => {
      const docRef = doc(db, REVIEWS_COLLECTION, rev.id);
      batch.set(docRef, cleanFirestoreData(rev), { merge: true });
    });
    await batch.commit();
    setLocalCachedReviews(reviews);
    localStorage.setItem(REVIEWS_INIT_KEY, 'true');
  } catch (e) {
    console.warn('Notice seeding reviews to Firestore:', e);
    setLocalCachedReviews(reviews);
  }
}

export async function resetReviewsDb(): Promise<void> {
  try {
    const current = await getCollection<CustomerReview>(REVIEWS_COLLECTION);
    const batch = writeBatch(db);
    current.forEach((rev) => {
      batch.delete(doc(db, REVIEWS_COLLECTION, rev.id));
    });
    await batch.commit();
    await seedReviewsDb(INITIAL_REVIEWS);
  } catch (e) {
    console.error('Error resetting reviews:', e);
    setLocalCachedReviews(INITIAL_REVIEWS);
    localStorage.setItem(REVIEWS_INIT_KEY, 'true');
  }
}

// 7. WhatsApp Formatting Helpers
export function generateAdminOrderWhatsAppUrl(order: Order, adminPhone: string = '923229768391'): string {
  const itemsText = order.items
    .map((item) => `• ${item.title} (${item.flavor || 'Vanilla'}) × ${item.quantity} = Rs. ${(item.price * item.quantity).toLocaleString()}`)
    .join('\n');

  const message = `*NEW HEALTH STORE ORDER* 🛍️

*Order Number:* ${order.orderNumber}
*Customer Name:* ${order.customer.fullName}
*Phone:* ${order.customer.phone}
*Address:* ${order.customer.address}
*City:* ${order.customer.city}
${order.customer.notes ? `*Notes:* ${order.customer.notes}\n` : ''}
*Products:*
${itemsText}

*Subtotal:* Rs. ${order.subtotal.toLocaleString()} PKR
*Delivery:* Rs. ${order.shippingFee.toLocaleString()} PKR
*TOTAL:* Rs. ${order.total.toLocaleString()} PKR
*Payment:* Cash on Delivery (COD)
*Order Time:* ${order.createdAt}

_Status: ${order.status}_`;

  return `https://wa.me/${adminPhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
}

export function generateCustomerConfirmWhatsAppUrl(order: Order, storePhone: string = '923229768391'): string {
  const message = `Hello Health Store! 🙋‍♂️ I want to confirm my order *${order.orderNumber}*.
Name: ${order.customer.fullName}
Total: Rs. ${order.total.toLocaleString()} PKR (Cash on Delivery)
Please dispatch my parcel as soon as possible. Thank you!`;

  return `https://wa.me/${storePhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
}

export function generateAdminCustomerContactWhatsAppUrl(order: Order): string {
  const cleanPhone = order.customer.phone.replace(/[^0-9]/g, '');
  const formattedPhone = cleanPhone.startsWith('0') ? `92${cleanPhone.slice(1)}` : cleanPhone;
  const message = `Assalam o Alaikum ${order.customer.fullName}! Health Store customer support team here regarding your Order #${order.orderNumber} (Rs. ${order.total.toLocaleString()} PKR). Is your delivery address: "${order.customer.address}, ${order.customer.city}" confirmed?`;

  return `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
}

// 8. Firebase Storage File Management with Instant Non-Blocking Timeout and Progress Tracking
export async function uploadFileToStorage(
  file: File | Blob,
  customPath: string,
  onProgress?: (percent: number) => void
): Promise<{ downloadUrl: string; storagePath: string }> {
  // First convert to Data URL immediately for zero-latency preview and persistence
  const getDataUrl = (): Promise<string> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = (e.target?.result as string) || '';
        resolve(base64);
      };
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });

  const fallbackDataUrl = await getDataUrl();

  try {
    const fileRef = storageRef(storage, customPath);
    const contentType = (file as File).type || 'image/webp';
    
    // Use uploadBytesResumable to track real-time upload progress (0% -> 25% -> 50% -> 75% -> 100%)
    const uploadTask = uploadBytesResumable(fileRef, file, { contentType });
    
    return await new Promise<{ downloadUrl: string; storagePath: string }>((resolve) => {
      // Set a 8-second safety fallback timeout so slow connections never freeze the UI
      const safetyTimer = setTimeout(() => {
        if (onProgress) onProgress(100);
        resolve({ downloadUrl: fallbackDataUrl, storagePath: customPath });
      }, 8000);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (snapshot.totalBytes > 0) {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            if (onProgress) onProgress(progress);
          }
        },
        (error) => {
          clearTimeout(safetyTimer);
          console.warn('Firebase Storage upload notice, using instant direct image:', error);
          if (onProgress) onProgress(100);
          resolve({ downloadUrl: fallbackDataUrl, storagePath: customPath });
        },
        async () => {
          clearTimeout(safetyTimer);
          try {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            if (onProgress) onProgress(100);
            resolve({ downloadUrl, storagePath: customPath });
          } catch (e) {
            resolve({ downloadUrl: fallbackDataUrl, storagePath: customPath });
          }
        }
      );
    });
  } catch (storageError) {
    console.warn('Firebase Storage upload notice, using instant direct image:', storageError);
    if (onProgress) onProgress(100);
    return { downloadUrl: fallbackDataUrl, storagePath: customPath };
  }
}

export async function deleteFileFromStorage(storagePathOrUrl: string): Promise<void> {
  if (!storagePathOrUrl) return;
  
  // If it's a storage path like "products/..." or "hero/..."
  try {
    if (!storagePathOrUrl.startsWith('data:') && !storagePathOrUrl.startsWith('http://') && !storagePathOrUrl.startsWith('https://')) {
      const fileRef = storageRef(storage, storagePathOrUrl);
      await deleteObject(fileRef);
    } else if (storagePathOrUrl.includes('firebasestorage.googleapis.com')) {
      // Decode path from Firebase Storage URL
      const fileRef = storageRef(storage, storagePathOrUrl);
      await deleteObject(fileRef);
    }
  } catch (err) {
    console.warn(`Could not delete storage object at ${storagePathOrUrl}:`, err);
  }
}

// 9. Hero Slides & Banners Firestore Service
const HERO_SLIDES_COLLECTION = 'hero_slides';
const LOCAL_HERO_KEY = 'healthstore_custom_hero_slides_v3';
const HERO_INIT_KEY = 'healthstore_hero_initialized_v3';

export const INITIAL_HERO_SLIDES: HeroSlide[] = [
  {
    id: 'hero-slide-1',
    title: '',
    subtitle: '',
    buttonText: '',
    buttonLink: 'shop-all',
    desktopImageUrl: ASSET_IMAGES.fullBanner,
    order: 1,
    active: true,
    badge: '',
    createdAt: new Date().toISOString(),
  },
];

function hydrateHeroSlide(slide: HeroSlide): HeroSlide {
  const isDesktopValid =
    typeof slide.desktopImageUrl === 'string' &&
    slide.desktopImageUrl.trim().length > 5 &&
    !slide.desktopImageUrl.includes('undefined') &&
    !slide.desktopImageUrl.includes('null') &&
    !slide.desktopImageUrl.startsWith('blob:');

  const isMobileValid =
    typeof slide.mobileImageUrl === 'string' &&
    slide.mobileImageUrl.trim().length > 5 &&
    !slide.mobileImageUrl.includes('undefined') &&
    !slide.mobileImageUrl.includes('null') &&
    !slide.mobileImageUrl.startsWith('blob:');

  return {
    ...slide,
    desktopImageUrl: isDesktopValid ? slide.desktopImageUrl.trim() : ASSET_IMAGES.fullBanner,
    mobileImageUrl: isMobileValid ? slide.mobileImageUrl.trim() : undefined,
    active: slide.active !== false,
  };
}

export function getLocalCachedHeroSlides(): HeroSlide[] {
  try {
    const isCleared = localStorage.getItem(HERO_INIT_KEY) === 'cleared';
    const saved = localStorage.getItem(LOCAL_HERO_KEY);
    if (saved !== null) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const hydrated = parsed.map(hydrateHeroSlide);
        const active = hydrated.filter((s) => s.active !== false);
        if (active.length > 0) return hydrated;
      }
    }
    if (isCleared) return INITIAL_HERO_SLIDES;
  } catch (e) {
    console.warn('Could not read cached hero slides:', e);
  }
  return INITIAL_HERO_SLIDES;
}

export function setLocalCachedHeroSlides(slides: HeroSlide[]): void {
  try {
    const list = Array.isArray(slides) && slides.length > 0 ? slides : INITIAL_HERO_SLIDES;
    localStorage.setItem(LOCAL_HERO_KEY, JSON.stringify(list));
    localStorage.setItem(HERO_INIT_KEY, 'true');
  } catch (e) {
    console.warn('Could not save cached hero slides:', e);
  }
}

export async function fetchHeroSlidesFromDb(): Promise<HeroSlide[]> {
  try {
    const slides = await getCollection<HeroSlide>(HERO_SLIDES_COLLECTION);
    if (!slides || slides.length === 0) {
      await seedHeroSlidesDb(INITIAL_HERO_SLIDES).catch(() => {});
      setLocalCachedHeroSlides(INITIAL_HERO_SLIDES);
      return INITIAL_HERO_SLIDES;
    }
    const hydrated = slides.map(hydrateHeroSlide);
    const sorted = [...hydrated].sort((a, b) => (a.order || 0) - (b.order || 0));
    const active = sorted.filter((s) => s.active !== false);
    const finalList = active.length > 0 ? sorted : INITIAL_HERO_SLIDES;
    setLocalCachedHeroSlides(finalList);
    return finalList;
  } catch (err) {
    console.error('Error in fetchHeroSlidesFromDb:', err);
    return getLocalCachedHeroSlides();
  }
}

export function subscribeHeroSlidesDb(
  onData: (slides: HeroSlide[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  let isFirstSnap = true;
  return subscribeToCollection<HeroSlide>(
    HERO_SLIDES_COLLECTION,
    (slides) => {
      if (!slides || slides.length === 0) {
        if (isFirstSnap) {
          seedHeroSlidesDb(INITIAL_HERO_SLIDES).catch(console.error);
        }
        setLocalCachedHeroSlides(INITIAL_HERO_SLIDES);
        onData(INITIAL_HERO_SLIDES);
      } else {
        const hydrated = slides.map(hydrateHeroSlide);
        const sorted = [...hydrated].sort((a, b) => (a.order || 0) - (b.order || 0));
        const active = sorted.filter((s) => s.active !== false);
        const finalList = active.length > 0 ? sorted : INITIAL_HERO_SLIDES;
        setLocalCachedHeroSlides(finalList);
        onData(finalList);
      }
      isFirstSnap = false;
    },
    (err) => {
      onData(getLocalCachedHeroSlides());
      if (onError) onError(err);
    }
  );
}

export async function saveHeroSlideDb(slide: HeroSlide): Promise<void> {
  const id = slide.id || `slide-${Date.now()}`;
  const prepared: HeroSlide = {
    ...slide,
    id,
    order: Number(slide.order) || 1,
    active: slide.active !== false,
  };

  const list = getLocalCachedHeroSlides();
  const index = list.findIndex((s) => s.id === id);
  const updated = index >= 0 ? [...list] : [...list, prepared];
  if (index >= 0) updated[index] = prepared;
  const sorted = updated.sort((a, b) => a.order - b.order);
  setLocalCachedHeroSlides(sorted);

  await setDocument(HERO_SLIDES_COLLECTION, id, prepared, true);
}

export async function deleteHeroSlideDb(slideId: string, storagePaths?: string[]): Promise<void> {
  const list = getLocalCachedHeroSlides();
  const updated = list.filter((s) => s.id !== slideId);
  setLocalCachedHeroSlides(updated);
  if (updated.length === 0) {
    localStorage.setItem(HERO_INIT_KEY, 'cleared');
  }
  
  if (storagePaths && storagePaths.length > 0) {
    for (const path of storagePaths) {
      if (path) await deleteFileFromStorage(path);
    }
  }

  await removeDocument(HERO_SLIDES_COLLECTION, slideId);
}

export async function reorderHeroSlidesDb(slides: HeroSlide[]): Promise<void> {
  try {
    const batch = writeBatch(db);
    const updated = slides.map((slide, index) => {
      const reorderedSlide = { ...slide, order: index + 1 };
      const docRef = doc(db, HERO_SLIDES_COLLECTION, slide.id);
      batch.set(docRef, cleanFirestoreData(reorderedSlide), { merge: true });
      return reorderedSlide;
    });
    await batch.commit();
    setLocalCachedHeroSlides(updated);
  } catch (e) {
    console.warn('Notice reordering hero slides in Firestore:', e);
    setLocalCachedHeroSlides(slides);
  }
}

export async function seedHeroSlidesDb(slides: HeroSlide[]): Promise<void> {
  try {
    const batch = writeBatch(db);
    slides.forEach((slide) => {
      const docRef = doc(db, HERO_SLIDES_COLLECTION, slide.id);
      batch.set(docRef, cleanFirestoreData(slide), { merge: true });
    });
    await batch.commit();
    setLocalCachedHeroSlides(slides);
  } catch (e) {
    console.warn('Notice seeding hero slides to Firestore:', e);
    setLocalCachedHeroSlides(slides);
  }
}

export default app;
